<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { Component } from 'vue';
import {
  CircleCheckBig,
  Home,
  Info,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';
import { useContentStore } from '@/stores/content';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon =
  (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileTextIcon;

const PAGE_LINK_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  home: Home,
  info: Info,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon ?? FileCheck,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
};

function iconForName(iconName: string): Component {
  const fallback = FileTextIcon ?? Info;
  return PAGE_LINK_ICON_MAP[iconName] ?? fallback;
}

export type PageLinksPayload = {
  pages?: { page_id?: number }[];
} | null;

const props = defineProps<{
  pageLinks?: PageLinksPayload;
  /** When featured (home header band), pull cards up to overlap (prototype QuickAccess). */
  variant: 'minimal' | 'featured';
}>();

const contentStore = useContentStore();

const resolvedItems = computed(() => {
  const raw = props.pageLinks?.pages;
  if (!raw?.length) return [];
  const out: {
    pageId: number;
    title: string;
    description: string;
    url: string;
    icon: Component;
  }[] = [];
  for (const entry of raw) {
    const id = entry.page_id;
    if (id == null) continue;
    const row = contentStore.getPageById(id);
    if (!row) continue;
    const desc = row.meta?.search_description?.trim() ?? '';
    out.push({
      pageId: id,
      title: row.title,
      description: desc,
      url: contentStore.getUrlForPageId(id),
      icon: iconForName((row.meta?.icon ?? 'info').trim() || 'info'),
    });
  }
  return out;
});
</script>

<template>
  <div
    v-if="resolvedItems.length"
    class="page-quick-links"
    :class="{ 'page-quick-links--overlap': variant === 'featured' }"
  >
    <div class="page-quick-links__inner">
      <div class="page-quick-links__grid">
        <RouterLink
          v-for="item in resolvedItems"
          :key="item.pageId"
          :to="item.url"
          class="page-quick-links__card"
        >
          <div class="page-quick-links__icon-wrap">
            <component :is="item.icon" class="page-quick-links__icon" aria-hidden="true" />
          </div>
          <h2 class="page-quick-links__title">{{ item.title }}</h2>
          <p v-if="item.description" class="page-quick-links__description">
            {{ item.description }}
          </p>
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-quick-links {
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}

.page-quick-links--overlap {
  margin-top: -2rem;
}
@media (min-width: 640px) {
  .page-quick-links--overlap {
    margin-top: -2.5rem;
  }
}
@media (min-width: 1024px) {
  .page-quick-links--overlap {
    margin-top: -3rem;
  }
}

.page-quick-links__inner {
  width: 100%;
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
  box-sizing: border-box;
}
@media (min-width: 640px) {
  .page-quick-links__inner {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .page-quick-links__inner {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

.page-quick-links__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  position: relative;
  z-index: 10;
}
@media (min-width: 640px) {
  .page-quick-links__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 1024px) {
  .page-quick-links__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.page-quick-links__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100%;
  text-align: center;
  padding: 1.25rem 1.25rem;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  text-decoration: none;
  color: inherit;
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;
}
@media (min-width: 640px) {
  .page-quick-links__card {
    padding: 1.5rem 1.5rem;
  }
}

.page-quick-links__card:hover {
  background: var(--color-bg-muted);
  border-color: rgba(4, 120, 87, 0.45);
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  transform: translateY(-4px);
}

.page-quick-links__icon-wrap {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-sm);
  background: rgba(4, 120, 87, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 0.75rem;
  transition: background-color 0.3s ease;
}
.page-quick-links__card:hover .page-quick-links__icon-wrap {
  background: rgba(4, 120, 87, 0.15);
}

.page-quick-links__icon {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--color-primary);
}

.page-quick-links__title {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.375;
  transition: color 0.3s ease;
}
.page-quick-links__card:hover .page-quick-links__title {
  color: var(--color-primary);
}
@media (min-width: 640px) {
  .page-quick-links__title {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }
}

.page-quick-links__description {
  margin: 0;
  flex: 1 1 auto;
  font-size: 0.875rem;
  line-height: 1.625;
  color: var(--color-text-muted);
}
</style>
